///////////////////////////////////////////////////////////////////////////////////////
//                          Copyright (c) 2021 - 2024
//                           Coyote Logistics L.L.C.
//                         All Rights Reserved Worldwide
//
//WARNING:  This program (or document) is unpublished, proprietary
//property of Coyote Logistics L.L.C. and is to be maintained in strict confidence.
//Unauthorized reproduction, distribution or disclosure of this program
//(or document), or any program (or document) derived from it is
//prohibited by State and Federal law, and by local law outside of the U.S.
///////////////////////////////////////////////////////////////////////////////////////

import { createSlice } from "@reduxjs/toolkit";
import { orderBy, uniq } from "lodash";

import {
  getMatchingLanes,
  getSpecificMatchingLanes,
  patchEuLaneMatchEdits,
  patchLaneMatchEdits
} from "../api/lanes";
import { division, laneType, order } from "../helpers/types";
import pricingSliceHelper from "../helpers/pricingSliceHelper";
import getPricingGridColumns from "../helpers/getPricingGridColumns";
import handlePricingCellChange from "../helpers/handlePricingCellChange";
import getDefaultLaneMatchFilters from "../helpers/getDefaultLaneMatchFilters";
import messages from "../helpers/messages";
import bindEditedLanes from "../helpers/bindEditedLanes";
import {
  fetchDisabledPricingLanes,
  toggleDisableLane as toggleDisableLaneBidPricing
} from "./bidPricingSlice";

export const initialState = {
  matchingLaneRequestLaneId: null,
  lanes: null,
  lanesLoading: false,
  lanesError: false,
  totalLanes: null,
  averageMargin: null,
  isCoyoteBid: undefined,
  division: undefined,
  useRxoTlServerPagedLanes: false,
  sortField: null,
  sortOrder: order.ascending,
  searchTerm: "",
  offset: 0,
  perPage: 50,
  totalFiltered: 0,
  columnFilters: {},
  rangeFilters: {},
  columnFilterMenu: { field: null, visible: false },
  filtersApplied: false,
  columnWidths: {},
  columns: {
    changed: false,
    order: []
  },
  saveLaneEditsLoading: false,
  saveLaneEditsFailure: false,
  filters: {
    odhRadiusDistance: null,
    ddhRadiusDistance: null,
    minVolume: null,
    maxVolume: null,
    minCost: null,
    monthRange: null,
    statuses: null
  },
  cancellationDetails: {
    newTruckloadLaneId: null,
    showModal: false
  }
};

const laneMatchSlice = createSlice({
  name: "laneMatch",
  initialState,
  reducers: {
    requestMatchingLanes: (state, action) => {
      state.division = action.payload.division;
      state.matchingLaneRequestLaneId =
        action.payload.truckloadLane.truckloadLaneId;
      if (action.payload.resetLaneMatchFilters) {
        state.filters = getDefaultLaneMatchFilters();
      }
      state.cancellationDetails = initialState.cancellationDetails;
      pricingSliceHelper.requestPricingLanes(
        state,
        {
          payload: {
            clearFilterAndSortDetails: action.payload.clearFilterAndSortDetails,
            isCoyoteBid: action.payload.isCoyoteBid
          }
        },
        initialState
      );
    },
    receiveMatchingLanesSuccess: (state, action) =>
      pricingSliceHelper.receivePricingLanesSuccess(
        state,
        action,
        initialState
      ),
    receiveMatchingLanesFailure: state =>
      pricingSliceHelper.receivePricingLanesFailure(state),
    loadColumnOrder: (
      state,
      { payload: { role, enableInternalCapacityMatching, isCoyoteBid } }
    ) => {
      if (isCoyoteBid !== undefined) {
        state.isCoyoteBid = isCoyoteBid;
      }
      pricingSliceHelper.loadColumns(
        state,
        orderBy(
          enableInternalCapacityMatching
            ? getPricingGridColumns(role, state.division, null, state.isCoyoteBid).filter(
                c => c.laneMatchOrder && !c.isICM
              )
            : getPricingGridColumns(role, state.division, null, state.isCoyoteBid).filter(
                c => c.laneMatchOrder
              ),
          "laneMatchOrder"
        ).map(c => c.field),
        localStorage.laneMatchColumnOrder
      );
    },
    setColumnOrder: (state, action) => {
      pricingSliceHelper.setColumnOrder(
        state,
        action,
        getPricingGridColumns(action.payload.role, state.division, null, state.isCoyoteBid).map(
          c => c.field
        )
      );
      localStorage.laneMatchColumnOrder = JSON.stringify(state.columns.order);
    },
    toggleColumnFilterMenu: pricingSliceHelper.toggleColumnFilterMenu,
    filterPricingLanes: pricingSliceHelper.filterPricingLanes,
    setColumnWidth: pricingSliceHelper.setColumnWidth,
    sortPricingLanes: pricingSliceHelper.sortPricingLanes,
    handleCellChange: handlePricingCellChange,
    resetLaneMatchState: () => initialState,
    resetLaneMatchGridFilters: state => {
      pricingSliceHelper.resetPricingGridFilters(state, initialState);
    },
    setLaneMatchFilter: (state, { payload }) => {
      state.filters = payload;
    },
    requestSaveLaneEdits: pricingSliceHelper.requestSaveLaneEdits,
    saveLaneEditsFailure: pricingSliceHelper.saveLaneEditsFailure,
    saveLaneEditsReset: pricingSliceHelper.saveLaneEditsReset,
    toggleConfirmCancellationModal: (
      state,
      { payload: { showModal, newTruckloadLaneId } }
    ) => {
      state.cancellationDetails.showModal = showModal;
      state.cancellationDetails.newTruckloadLaneId = showModal
        ? newTruckloadLaneId
        : null;
    },
    toggleDisableLane: pricingSliceHelper.toggleDisableLane,
    replaceDisabledLanes: pricingSliceHelper.replaceDisabledLanes
  }
});

export const {
  requestMatchingLanes,
  receiveMatchingLanesSuccess,
  receiveMatchingLanesFailure,
  loadColumnOrder,
  setColumnOrder,
  toggleColumnFilterMenu,
  filterPricingLanes,
  setColumnWidth,
  sortPricingLanes,
  handleCellChange,
  resetLaneMatchState,
  resetLaneMatchGridFilters,
  setLaneMatchFilter,
  requestSaveLaneEdits,
  saveLaneEditsFailure,
  saveLaneEditsReset,
  toggleConfirmCancellationModal,
  toggleDisableLane,
  replaceDisabledLanes
} = laneMatchSlice.actions;

export const laneMatchReducer = laneMatchSlice.reducer;

export const fetchMatchingLanes =
  ({
    role,
    truckloadLane,
    clearFilterAndSortDetails = false,
    resetLaneMatchFilters = true,
    discardUnsavedChanges = false
  }) =>
  async (dispatch, getState) => {
    const {
      laneMatch: { lanes, cancellationDetails },
      bidPricing: { division: bidDivision },
      featureFlags: { enableInternalCapacityMatching }
    } = getState();
    if (
      !discardUnsavedChanges &&
      lanes &&
      lanes.find(l => l.metaData.laneChanged) &&
      !cancellationDetails.showModal
    ) {
      dispatch(
        toggleConfirmCancellationModal({
          showModal: true,
          newTruckloadLaneId: truckloadLane.truckloadLaneId
        })
      );
    } else {
      const isCoyoteBid = getState().bidSummary.summary?.isCoyoteBid;
      dispatch(
        requestMatchingLanes({
          clearFilterAndSortDetails,
          truckloadLane,
          resetLaneMatchFilters,
          division: bidDivision,
          isCoyoteBid
        })
      );
      dispatch(loadColumnOrder({ role, enableInternalCapacityMatching, isCoyoteBid }));
      try {
        let { data } = await getMatchingLanes(
          getState().laneMatch.filters,
          truckloadLane.truckloadLaneId,
          getState().bidSummary.summary?.bidId
        );
        if (bidDivision === division.eu) {
          data = data.map(l => ({ ...l, truckloadLaneId: l.id }));
        }
        dispatch(
          receiveMatchingLanesSuccess({
            lanesResponse: { lanes: data },
            requestedLaneType: laneType.standardTruckload,
            role: role,
            clearFilterAndSortDetails
          })
        );
        if (clearFilterAndSortDetails === false) {
          dispatch(
            filterPricingLanes({
              applyOnExistingFilters: true,
              role
            })
          );
          dispatch(sortPricingLanes({ field: "" }));
          dispatch(fetchDisabledPricingLanes());
        }
      } catch (exception) {
        if (exception.message !== messages.cancelledByUser.defaultMessage) {
          dispatch(receiveMatchingLanesFailure());
        }
      }
    }
  };

export const saveEditedLane = role => async (dispatch, getState) => {
  dispatch(requestSaveLaneEdits());
  const { laneMatch, bidPricing } = getState();
  let tlLaneEditRequests = [];

  uniq(
    laneMatch.lanes
      .filter(l => l.metaData.laneChanged)
      .map(({ bidId }) => bidId)
  ).forEach(bidId =>
    tlLaneEditRequests.push({
      bidId,
      laneEdits: bindEditedLanes({
        lanes: laneMatch.lanes.filter(l => l.bidId === bidId)
      })
    })
  );

  try {
    await (bidPricing.division === division.eu
      ? patchEuLaneMatchEdits(tlLaneEditRequests)
      : patchLaneMatchEdits(tlLaneEditRequests));
    dispatch(fetchDisabledPricingLanes());
    dispatch(
      fetchMatchingLanes({
        role,
        truckloadLane: bidPricing.lanes.find(
          l => l.truckloadLaneId === laneMatch.matchingLaneRequestLaneId
        ),
        clearFilterAndSortDetails: false,
        resetLaneMatchFilters: false,
        discardUnsavedChanges: true
      })
    );
  } catch {
    dispatch(saveLaneEditsFailure());
  }
};

export const changeCellAndDisableIfRequiredLaneMatch =
  payload => async (dispatch, getState) => {
    dispatch(handleCellChange(payload));
    const { laneMatch, bidPricing } = getState();
    const { truckloadLaneId } = laneMatch.lanes[payload.index];

    if (bidPricing.lanes.some(l => l.truckloadLaneId === truckloadLaneId)) {
      dispatch(
        toggleDisableLaneBidPricing([
          {
            truckloadLaneId,
            disabled: laneMatch.lanes[payload.index].metaData.laneChanged
          }
        ])
      );
    }
  };

export const disableMatchingLanes = () => (dispatch, getState) => {
  const { laneMatch, bidPricing } = getState();
  const conflictedLanes = bidPricing.lanes
    .filter(
      l =>
        l.metaData.laneChanged &&
        laneMatch.lanes?.some(
          ({ truckloadLaneId }) => l.truckloadLaneId === truckloadLaneId
        )
    )
    .map(l => ({ truckloadLaneId: l.truckloadLaneId, disabled: true }));
  dispatch(toggleDisableLane(conflictedLanes));
};

export const fetchDisabledMatchingLanes = () => async (dispatch, getState) => {
  const {
    laneMatch: { lanes },
    bidSummary: { summary }
  } = getState();
  const disabledLaneIds = lanes
    ?.filter(l => l.metaData.disabled)
    .map(l => l.truckloadLaneId);
  if (disabledLaneIds?.length > 0) {
    try {
      const response = await getSpecificMatchingLanes(
        summary?.bidId,
        disabledLaneIds
      );
      if (summary?.division === division.eu) {
        response.data = response.data.map(l => ({
          ...l,
          truckloadLaneId: l.id
        }));
      }
      dispatch(replaceDisabledLanes({ updatedLanes: response.data }));
    } catch {
      dispatch(receiveMatchingLanesFailure());
    }
  }
};
